import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { ArrowLeft } from "lucide-react";

/* Privacy policy public page. Required for Google Play & App Store review.
   Routes: /privacy (EN) and /privacidad (ES). Both render in the active language. */
export default function PrivacyPolicy() {
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="privacy-page">
      <header className="bg-[var(--ink)] text-white">
        <div className="max-w-3xl mx-auto px-5 py-6 flex items-center justify-between gap-3">
          <button
            onClick={() => nav(-1)}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Atrás"
          >
            <ArrowLeft size={18} />
          </button>
          <Link to="/login" className="flex items-center gap-3 flex-1">
            <img src="/images/logo-brand.png" alt="Smile & Shine" className="h-10 rounded-lg" />
            <span className="font-bold text-sm hidden sm:inline">Smile &amp; Shine Cleaning</span>
          </Link>
          <div className="inline-flex bg-white/10 rounded-full p-1" data-testid="privacy-lang-switch">
            <button
              onClick={() => setLang("es")}
              className={`px-2.5 py-1 rounded-full text-xs font-bold ${lang === "es" ? "bg-white text-[var(--ink)]" : "text-white/80"}`}
            >🇪🇸 ES</button>
            <button
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 rounded-full text-xs font-bold ${lang === "en" ? "bg-white text-[var(--ink)]" : "text-white/80"}`}
            >🇬🇧 EN</button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10 prose prose-slate">
        {lang === "es" ? <SpanishText /> : <EnglishText />}
        <footer className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500">
          © {new Date().getFullYear()} Smile &amp; Shine Cleaning · Mallorca, España ·
          {" "}<a href="mailto:info@smileandshine.es" className="text-[var(--coral)] hover:underline">info@smileandshine.es</a>
        </footer>
      </main>
    </div>
  );
}

const H1 = (p) => <h1 className="sns-title text-3xl md:text-4xl mb-2" {...p} />;
const H2 = (p) => <h2 className="sns-title text-xl mt-8 mb-3" {...p} />;
const P = (p) => <p className="text-slate-700 leading-relaxed mb-3" {...p} />;
const Strong = (p) => <strong className="text-slate-900" {...p} />;
const UL = (p) => <ul className="list-disc list-inside text-slate-700 space-y-1 mb-3" {...p} />;

function SpanishText() {
  return (
    <article>
      <H1>Política de Privacidad</H1>
      <P><Strong>Última actualización:</Strong> 19 de mayo de 2026</P>
      <P><Strong>Responsable:</Strong> Smile &amp; Shine Cleaning · Mallorca, España</P>
      <P><Strong>Contacto:</Strong> <a href="mailto:info@smileandshine.es" className="text-[var(--coral)]">info@smileandshine.es</a></P>

      <P>Esta política describe cómo Smile &amp; Shine Cleaning trata los datos personales de administradores y empleados que utilizan la aplicación móvil y web.</P>

      <H2>1. Datos que recopilamos</H2>
      <UL>
        <li><Strong>Cuenta:</Strong> nombre, email, contraseña cifrada (bcrypt), rol y tarifa horaria.</li>
        <li><Strong>Operativa:</Strong> mantenimientos asignados, horas de check-in/check-out, pausas, fotos, observaciones, incidencias, checklists.</li>
        <li><Strong>Clientes y propiedades:</Strong> nombre, dirección, CIF, teléfono, embarcaciones, matrículas, puertos.</li>
        <li><Strong>Ubicación (opcional):</Strong> coordenadas GPS aproximadas al iniciar/finalizar un servicio. Sólo con permiso explícito.</li>
        <li><Strong>Fotos:</Strong> capturadas con cámara o seleccionadas, ligadas al mantenimiento.</li>
        <li><Strong>Técnicos:</Strong> identificador anónimo de dispositivo, tipo de S.O.</li>
      </UL>
      <P>No recopilamos datos sensibles ni de menores ni ubicación en segundo plano.</P>

      <H2>2. Base legal (RGPD)</H2>
      <UL>
        <li><Strong>Contrato</Strong> (art. 6.1.b) — gestión de tu cuenta y los servicios.</li>
        <li><Strong>Consentimiento</Strong> (art. 6.1.a) — para cámara y geolocalización. Revocable desde ajustes del dispositivo.</li>
        <li><Strong>Interés legítimo</Strong> (art. 6.1.f) — para detectar fraude y mejorar la app.</li>
      </UL>

      <H2>3. Quién accede</H2>
      <UL>
        <li>El administrador de tu empresa.</li>
        <li>Personal técnico autorizado de Smile &amp; Shine.</li>
        <li><Strong>Holded</Strong> (facturación) — sólo datos necesarios para presupuestos. <a href="https://www.holded.com/es/legal/privacidad" className="text-[var(--coral)]" target="_blank" rel="noopener noreferrer">Política Holded</a>.</li>
        <li>Proveedor de hosting (servidores en la UE).</li>
      </UL>
      <P><Strong>No vendemos tus datos.</Strong></P>

      <H2>4. Conservación</H2>
      <UL>
        <li>Cuenta: mientras esté activa + 12 meses por motivos fiscales.</li>
        <li>Mantenimientos y fotos: 5 años (obligación contable).</li>
        <li>Logs técnicos: 30 días.</li>
      </UL>

      <H2>5. Tus derechos</H2>
      <P>Puedes ejercer tus derechos de acceso, rectificación, supresión, limitación, oposición y portabilidad escribiendo a <a href="mailto:info@smileandshine.es" className="text-[var(--coral)]">info@smileandshine.es</a>. También puedes reclamar ante la <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[var(--coral)]">AEPD</a>.</P>

      <H2>6. Cookies y almacenamiento local</H2>
      <P>Usamos <code>localStorage</code> para mantener tu sesión (JWT 365 días) y el idioma. No usamos cookies publicitarias ni de terceros.</P>

      <H2>7. Seguridad</H2>
      <UL>
        <li>Contraseñas con hash bcrypt.</li>
        <li>Conexión HTTPS/TLS.</li>
        <li>JWT firmado con expiración.</li>
        <li>Acceso por rol (admin/empleado).</li>
      </UL>

      <H2>8. Cambios</H2>
      <P>Te avisaremos con al menos 15 días de antelación si cambia esta política.</P>
    </article>
  );
}

function EnglishText() {
  return (
    <article>
      <H1>Privacy Policy</H1>
      <P><Strong>Last updated:</Strong> May 19, 2026</P>
      <P><Strong>Controller:</Strong> Smile &amp; Shine Cleaning · Mallorca, Spain</P>
      <P><Strong>Contact:</Strong> <a href="mailto:info@smileandshine.es" className="text-[var(--coral)]">info@smileandshine.es</a></P>

      <P>This policy explains how Smile &amp; Shine Cleaning processes the personal data of admins and staff using the mobile and web application.</P>

      <H2>1. Data we collect</H2>
      <UL>
        <li><Strong>Account:</Strong> name, email, hashed password (bcrypt), role and hourly rate.</li>
        <li><Strong>Operations:</Strong> assigned jobs, check-in/check-out times, breaks, photos, notes, incidents, checklists.</li>
        <li><Strong>Customers &amp; properties:</Strong> name, address, tax ID, phone, vessels, registration, ports.</li>
        <li><Strong>Location (optional):</Strong> approximate GPS coordinates when starting/finishing a service. Only with explicit permission.</li>
        <li><Strong>Photos:</Strong> captured with camera or chosen, linked to the maintenance record.</li>
        <li><Strong>Technical:</Strong> anonymous device id, OS type.</li>
      </UL>
      <P>We do not collect sensitive data, minors' data, or background location.</P>

      <H2>2. Legal basis (GDPR)</H2>
      <UL>
        <li><Strong>Contract</Strong> (Art. 6.1.b) — account and service management.</li>
        <li><Strong>Consent</Strong> (Art. 6.1.a) — camera and location, revocable any time from device settings.</li>
        <li><Strong>Legitimate interest</Strong> (Art. 6.1.f) — fraud detection and product improvement.</li>
      </UL>

      <H2>3. Who accesses your data</H2>
      <UL>
        <li>Your company administrator.</li>
        <li>Authorised Smile &amp; Shine technical staff.</li>
        <li><Strong>Holded</Strong> (invoicing) — only data needed for quotes. <a href="https://www.holded.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[var(--coral)]">Holded policy</a>.</li>
        <li>Hosting provider (EU-based servers).</li>
      </UL>
      <P><Strong>We do not sell your data.</Strong></P>

      <H2>4. Retention</H2>
      <UL>
        <li>Account: while active + 12 months for tax/legal reasons.</li>
        <li>Jobs and photos: 5 years (accounting requirement).</li>
        <li>Technical logs: 30 days.</li>
      </UL>

      <H2>5. Your rights</H2>
      <P>Access, rectification, erasure, restriction, objection and portability — email <a href="mailto:info@smileandshine.es" className="text-[var(--coral)]">info@smileandshine.es</a>. You can also complain to the <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[var(--coral)]">Spanish DPA</a>.</P>

      <H2>6. Cookies and local storage</H2>
      <P>We use <code>localStorage</code> to keep your session (JWT 365 days) and language preference. No third-party tracking cookies.</P>

      <H2>7. Security</H2>
      <UL>
        <li>bcrypt password hashing.</li>
        <li>HTTPS/TLS connection.</li>
        <li>JWT signed tokens with expiry.</li>
        <li>Role-based access control.</li>
      </UL>

      <H2>8. Changes</H2>
      <P>We notify you at least 15 days before any change.</P>
    </article>
  );
}
